// ============================================================
// Expense Modal (Create & Edit Expense)
// ============================================================

import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, MenuItem, FormControl, InputLabel, Select, CircularProgress } from '@mui/material';

export function ExpenseModal({ open, onClose, onSubmit, loading, expense, categories = [] }) {
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0]);
  const [categoryId, setCategoryId] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [receiptUrl, setReceiptUrl] = useState('');

  useEffect(() => {
    if (expense) {
      setExpenseDate(expense.expense_date || new Date().toISOString().split('T')[0]);
      setCategoryId(expense.category_id || (expense.category?.id || ''));
      setAmount(expense.amount ? String(expense.amount) : '');
      setDescription(expense.description || '');
      setReceiptUrl(expense.receipt_url || '');
    } else {
      setExpenseDate(new Date().toISOString().split('T')[0]);
      setCategoryId(categories.length > 0 ? categories[0].id : '');
      setAmount('');
      setDescription('');
      setReceiptUrl('');
    }
  }, [expense, categories, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      expense_date: expenseDate,
      category_id: categoryId,
      amount: Number(amount),
      description: description.trim() || 'Expense Record',
      receipt_url: receiptUrl.trim() || '',
    };
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle fontWeight={700}>
          {expense ? 'Edit Expense Record' : 'Create New Expense'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Expense Date *"
              type="date"
              fullWidth
              required
              value={expenseDate}
              onChange={(e) => setExpenseDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <FormControl fullWidth required>
              <InputLabel>Category *</InputLabel>
              <Select
                value={categoryId}
                label="Category *"
                onChange={(e) => setCategoryId(e.target.value)}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name} {!cat.is_active ? '(Inactive)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Amount (₹) *"
              type="number"
              fullWidth
              required
              inputProps={{ min: 0, step: 'any' }}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />

            <TextField
              label="Description / Purpose"
              fullWidth
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            <TextField
              label="Receipt URL (Optional)"
              fullWidth
              placeholder="https://..."
              value={receiptUrl}
              onChange={(e) => setReceiptUrl(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit" disabled={loading}>
            Cancel
          </Button>

          <Button
            type="submit"
            variant="contained"
            disabled={loading || !categoryId || !amount}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ fontWeight: 600, px: 3 }}
          >
            {loading ? 'Saving...' : expense ? 'Update Expense' : 'Save Expense'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
